import { useMemo } from 'react';
import type { KitFontId, ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import type { StoryStyle } from 'fym-shared/src/music24/story/storyTypes';
import {
  lyricFitLimits,
  lyricFitState,
  type LyricFitAssetType,
  type LyricFitStateName,
} from 'fym-shared/src/music24/story/lyricFit';

export interface LyricFitView {
  state: LyricFitStateName;
  /** The characters of the value; a line break counts as one character, as a space. */
  chars: number;
  /** The most characters of a typical line at full size, in this theme. */
  fullChars: number;
  /** The share of the meter that the value fills (0 to 1). */
  barShare: number;
  /** The place of the full-size mark on the meter (0 to 1). */
  markShare: number;
}

/** The fit meter of the panel: the state comes from the font metrics of the theme (`lyricFitState`). */
export function useLyricFit(input: {
  value: string;
  assetTypeId: LyricFitAssetType;
  releaseThemeId: ReleaseThemeId;
  fontOverride: KitFontId | null;
  twoLines: boolean;
  /** The Story style: Type only has a larger lyric. Absent: the cover card. */
  storyStyle?: StoryStyle;
}): LyricFitView {
  const { value, assetTypeId, releaseThemeId, fontOverride, twoLines, storyStyle } = input;
  const limits = useMemo(
    () => lyricFitLimits({ assetTypeId, releaseThemeId, fontOverride, twoLines, storyStyle }),
    [assetTypeId, fontOverride, releaseThemeId, storyStyle, twoLines]
  );
  const fit = useMemo(
    () => lyricFitState({ text: value, assetTypeId, releaseThemeId, fontOverride, storyStyle }),
    [assetTypeId, fontOverride, releaseThemeId, storyStyle, value]
  );
  const chars = value.length;
  // The meter ends a little after the shrink limit, so a red value shows past it.
  const scale = Math.max(chars, Math.round(limits.floorChars * 1.15), 1);
  return {
    state: fit.state,
    chars,
    fullChars: limits.fullChars,
    barShare: Math.min(1, chars / scale),
    markShare: Math.min(1, limits.fullChars / scale),
  };
}
